import React, { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InvoicingState, Order } from 'models/order';
import { Box, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeviationList from 'components/order-details/main-order-view/deviation-section/deviation-list/deviation-list.component';
import AddUpdateDeviationDialog from 'components/order-details/main-order-view/deviation-section/add-update-deviation-dialog/add-update-deviation-dialog.component';
import { User } from 'models/user';
import {
    deviationsTemplate,
    orderDeviationsTemplate,
    addDeviationTemplate,
    updateDeviationTemplate,
    deleteDeviationTemplate,
    addDeviationActionTemplate,
    updateDeviationActionTemplate,
    deleteDeviationActionTemplate,
} from 'apollo/templates/deviation';
import { useMutation, useQuery, ApolloCache, Reference } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { Deviation, DeviationAction } from 'models/deviations';
import Loader from 'components/shared/loader/loader.component';
import ConfirmationDialog from 'components/orders/shared/confirmation-dialog.component';
import AddUpdateDeviationActionDialog from 'components/order-details/main-order-view/deviation-section/deviation-action/add-update-deviation-action-dialog/add-update-deviation-action-dialog.component';

type DeviationsProps = {
    order: Order;
    currentUser: User;
};
import { useRefreshOrder } from 'hooks/useRefreshOrder';
const Deviations: FC<DeviationsProps> = ({ order, currentUser }: DeviationsProps) => {
    // The order's deviation flag is server state: re-read the order after a change (DD-3231).
    const refreshOrder = useRefreshOrder();
    const { t: translate } = useTranslation(['deviations']);
    const [addDialogOpen, setDialogOpen] = useState(false);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [deviations, setDeviations] = useState<Deviation[]>([] as Deviation[]);
    const [deviation, setDeviation] = useState<Deviation>({} as Deviation);
    const [deviationId, setDeviationId] = useState('');
    const [deviationAction, setDeviationAction] = useState<DeviationAction>({} as DeviationAction);
    const [addActionDialogOpen, setActionDialogOpen] = useState(false);
    const [confirmActionDialogOpen, setConfirmActionDialogOpen] = useState(false);
    const [actionId, setActionId] = useState('');
    const { showBoundary } = useDenthubErrorBoundary();

    useQuery(deviationsTemplate, {
        variables: { orderId: order._id },
        onCompleted: ({ deviations }) => {
            setDeviations(deviations);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const sortDeviations = (deviationsArr: Deviation[]) => {
        return deviationsArr.sort((a, b) => new Date(b.addedOn).getTime() - new Date(a.addedOn).getTime());
    };

    // visibleForDentist changes add/remove items from the orderDeviations list, which Apollo's
    // cache cannot patch in place - refetch it so the Documents panel's "Deviation report" gating
    // (and the dentist-facing deviations view) don't require a manual page refresh to reflect the change.
    const orderDeviationsRefetch = [{ query: orderDeviationsTemplate, variables: { orderId: order._id } }];

    const [addDeviation, { loading: addDeviationLoading }] = useMutation(addDeviationTemplate, {
        refetchQueries: orderDeviationsRefetch,
        onError(error) {
            showBoundary(error);
        },
        onCompleted: ({ addDeviation }) => {
            const data = JSON.parse(JSON.stringify(deviations));
            data.push(addDeviation);
            sortDeviations(data);
            setDeviations(data);
            refreshOrder(order._id);
            setDialogOpen(false);
            setDeviation({} as Deviation);
        },
        update(cache: ApolloCache<Deviation>, { data: { addDeviation } }) {
            cache.modify({
                fields: {
                    deviations(deviationsRefs: Reference | (Reference | undefined)[], { toReference }) {
                        if (deviationsRefs instanceof Array) {
                            return [...deviationsRefs, toReference(addDeviation)];
                        }
                        return [deviationsRefs, toReference(addDeviation)];
                    },
                },
            });
        },
    });

    const [updateDeviation, { loading: updateDeviationLoading }] = useMutation(updateDeviationTemplate, {
        refetchQueries: orderDeviationsRefetch,
        onCompleted: ({ updateDeviation }) => {
            const data = JSON.parse(JSON.stringify(deviations));
            const index = data.findIndex((deviation: Deviation) => deviation._id === updateDeviation._id);
            if (index !== -1) {
                data.splice(index, 1, updateDeviation);
                sortDeviations(data);
                setDeviations(data);
                refreshOrder(order._id);
                setDialogOpen(false);
                setDeviation({} as Deviation);
            }
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [deleteDeviation, { loading: deleteDeviationLoading }] = useMutation(deleteDeviationTemplate, {
        refetchQueries: orderDeviationsRefetch,
        onError(error) {
            showBoundary(error);
        },
        onCompleted: ({ deleteDeviation }) => {
            const data = JSON.parse(JSON.stringify(deviations));
            const index = data.findIndex((deviation: Deviation) => deviation._id === deleteDeviation);
            if (index !== -1) {
                data.splice(index, 1);
                sortDeviations(data);
                setDeviations(data);
                refreshOrder(order._id);
                setDialogOpen(false);
                setDeviation({} as Deviation);
            }
        },
        update(cache: ApolloCache<Deviation[]>, { data: { deleteDeviation } }) {
            cache.modify({
                fields: {
                    deviations(existingDeviationsRefs: Reference | (Reference | undefined)[], { readField }) {
                        if (existingDeviationsRefs instanceof Array) {
                            return [...existingDeviationsRefs].filter((deviationsRef: Reference | undefined) => {
                                return deleteDeviation !== readField('_id', deviationsRef);
                            });
                        }
                        return [];
                    },
                },
            });
        },
    });

    const [addDeviationAction, { loading: addDeviationActionLoading }] = useMutation(addDeviationActionTemplate, {
        refetchQueries: orderDeviationsRefetch,
        onError(error) {
            showBoundary(error);
        },
        onCompleted: ({ addDeviationAction }) => {
            const data = JSON.parse(JSON.stringify(deviations));
            const deviationIndex = data.findIndex((deviation: Deviation) => {
                return deviation._id?.toString() === deviationId;
            });
            if (deviationIndex > -1) {
                data.splice(deviationIndex, 1, addDeviationAction);
                setDeviations(data);
                setActionDialogOpen(false);
                setDeviationAction({} as DeviationAction);
            }
        },
    });

    const [updateDeviationAction, { loading: updateDeviationActionLoading }] = useMutation(
        updateDeviationActionTemplate,
        {
            refetchQueries: orderDeviationsRefetch,
            onError(error) {
                showBoundary(error);
            },
            onCompleted: ({ updateDeviationAction }) => {
                const data = JSON.parse(JSON.stringify(deviations));
                const deviationIndex = data.findIndex((deviation: Deviation) => {
                    return deviation._id?.toString() === deviationId;
                });
                if (deviationIndex > -1) {
                    data.splice(deviationIndex, 1, updateDeviationAction);
                    setDeviations(data);
                    setActionDialogOpen(false);
                    setDeviationAction({} as DeviationAction);
                }
            },
        },
    );

    const [deleteDeviationAction, { loading: deleteDeviationActionLoading }] = useMutation(
        deleteDeviationActionTemplate,
        {
            refetchQueries: orderDeviationsRefetch,
            onError(error) {
                showBoundary(error);
            },
            onCompleted: ({ deleteDeviationAction }) => {
                const data = JSON.parse(JSON.stringify(deviations));
                const deviationIndex = data.findIndex((deviation: Deviation) => {
                    return deviation._id?.toString() === deviationId;
                });
                if (deviationIndex > -1) {
                    data.splice(deviationIndex, 1, deleteDeviationAction);
                    setDeviations(data);
                    setDeviationAction({} as DeviationAction);
                }
            },
        },
    );

    const onDialogClose = () => {
        setDialogOpen(false);
        setConfirmDialogOpen(false);
        setDeviation({} as Deviation);
    };

    const openDialog = () => {
        setDialogOpen(true);
    };

    const handleEditModal = (deviation: Deviation) => {
        setDeviation(deviation);
        setDialogOpen(true);
    };

    const openConfirmDialog = (deviationId: string) => {
        setDeviationId(deviationId);
        setConfirmDialogOpen(true);
    };

    const handleOk = async () => {
        await onDeviationDelete(deviationId);
        setConfirmDialogOpen(false);
    };

    const onActionDialogClose = () => {
        setActionDialogOpen(false);
        setConfirmActionDialogOpen(false);
        setDeviationAction({} as DeviationAction);
    };

    const openActionDialog = (deviationId: string) => {
        setDeviationId(deviationId);
        setActionDialogOpen(true);
    };

    const handleEditActionModal = (deviationId: string, deviationAction: DeviationAction) => {
        setDeviationAction(deviationAction);
        setDeviationId(deviationId);
        setActionDialogOpen(true);
    };

    const openConfirmActionDialog = (deviationId: string, actionId: string) => {
        setDeviationId(deviationId);
        setActionId(actionId);
        setConfirmActionDialogOpen(true);
    };

    const handleActionOk = async () => {
        await onDeviationActionDelete(deviationId, actionId);
        setConfirmActionDialogOpen(false);
    };

    const onDeviationAdd = async (orderDeviation: Deviation) => {
        orderDeviation.orderId = order._id;
        await addDeviation({
            variables: { orderDeviation },
        });
    };

    const onDeviationUpdate = async (orderDeviation: Deviation) => {
        orderDeviation.orderId = order._id;
        await updateDeviation({
            variables: { orderDeviation },
        });
    };

    const onDeviationDelete = async (deviationId: string) => {
        await deleteDeviation({
            variables: {
                orderId: order._id,
                deviationId: deviationId,
            },
        });
    };

    const onDeviationActionAdd = async (orderDeviationAction: DeviationAction, deviationId: string) => {
        orderDeviationAction.orderId = order._id;
        orderDeviationAction.deviationId = deviationId;
        await addDeviationAction({
            variables: { orderDeviationAction },
        });
    };

    const onDeviationActionUpdate = async (orderDeviationAction: DeviationAction, deviationId: string) => {
        orderDeviationAction.orderId = order._id;
        orderDeviationAction.deviationId = deviationId;
        await updateDeviationAction({
            variables: { orderDeviationAction },
        });
    };

    const onDeviationActionDelete = async (deviationId: string, actionId: string) => {
        await deleteDeviationAction({
            variables: {
                orderId: order._id,
                deviationId: deviationId,
                actionId: actionId,
            },
        });
    };

    if (
        addDeviationLoading ||
        updateDeviationLoading ||
        deleteDeviationLoading ||
        addDeviationActionLoading ||
        updateDeviationActionLoading ||
        deleteDeviationActionLoading
    ) {
        return <Loader />;
    }

    const modifyLocked = () => {
        return order.isProductionFinished || order.invoicingState !== InvoicingState.NOT_READY;
    };

    return (
        <Fragment>
            <Box>
                {!modifyLocked() ? (
                    <Box mb={1} flexGrow={1}>
                        <Button onClick={openDialog} data-test="deviations.add-btn">
                            {translate('deviations:ADD_DEVIATION')}
                        </Button>
                    </Box>
                ) : undefined}
                <DeviationList
                    deviations={deviations}
                    onOpenUpdateModal={handleEditModal}
                    onOpenConfirmDialog={openConfirmDialog}
                    onActionDialogOpen={openActionDialog}
                    onOpenUpdateActionModal={handleEditActionModal}
                    onOpenConfirmActionDialog={openConfirmActionDialog}
                    versionCheck={!modifyLocked()}
                />
            </Box>

            <AddUpdateDeviationDialog
                deviation={deviation}
                open={addDialogOpen}
                onDialogClose={onDialogClose}
                currentUser={currentUser}
                onDeviationAdd={onDeviationAdd}
                onDeviationUpdate={onDeviationUpdate}
            />
            <AddUpdateDeviationActionDialog
                deviationId={deviationId}
                deviationAction={deviationAction}
                open={addActionDialogOpen}
                onDialogClose={onActionDialogClose}
                onDeviationActionAdd={onDeviationActionAdd}
                onDeviationActionUpdate={onDeviationActionUpdate}
            />
            <ConfirmationDialog
                open={confirmDialogOpen}
                handleClose={onDialogClose}
                handleOk={handleOk}
                title={translate('deviations:DEVIATION_CONFIRM_TITLE')}
                confirm={translate('deviations:DEVIATION_CONFIRM_DELETE')}
                process={translate('common:DELETE')}
            />
            <ConfirmationDialog
                open={confirmActionDialogOpen}
                handleClose={onActionDialogClose}
                handleOk={handleActionOk}
                title={translate('deviations:DEVIATION_ACTION_CONFIRM_TITLE')}
                confirm={translate('deviations:DEVIATION_ACTION_CONFIRM_DELETE')}
                process={translate('common:DELETE')}
            />
        </Fragment>
    );
};

export default Deviations;
